import React from "react";
import PropTypes from "prop-types";

import {
  getCurrencySign,
  getPriceWithCurrency,
  getQuotePriceWithTaxes,
  getTotalPrice, sortByOrder,
  toFixedPrice
} from "../../../misc/helpers";
import {DISCOUNT_MAIN_TYPES, DISCOUNT_TYPES} from "../../priceWorksheet/discounstInterface";
import {
  discountSectionExistsInCurrentWizardSelections,
  getDepositAmount, getDepositDiscount, getDiscountCurrentValue,
  getDiscountsForSection,
  getDiscountSubTotalPrice,
  shouldCalculateDiscount
} from "../../priceWorksheet/discountFunctions";
import PrintDocumentDisclaimer from "./printDocumentDisclaimer";
import {getDifferentCurrencyCartItems, getPercentage} from "../../../misc/misc2";
import {isCafeCountetops} from "../../../misc/fabricatorHardcode";
import {
  getBalanceDueAmount,
  getFinancedAmount,
  getSpecificDiscount
} from "../../priceWorksheet/discountFinanceFunctions";

const PrintDocumentTotal = (props) => {
  const {styles, priceWorksheetState, fabricatorData, locale, cartItems, messages, printState, hasMultipleWorkSheets,
    quotes, wizardState} = props;
  const {View, Text} = window.ReactPdfRenderer;

  const {discountSections, customAddedDiscounts} = priceWorksheetState;
  const {receiptReferral} = printState;
  const fabricatorSettings = fabricatorData.settings;

  if (fabricatorData.isAmericanImport) {
    return null
  }

  let subTotalPrice = getTotalPrice(null, false).materialPrice;
  const totalPrice = getTotalPrice();
  let grandTotal = getQuotePriceWithTaxes(totalPrice);

  let allTaxes = totalPrice.priceTaxes.length
    ? totalPrice.priceTaxes
    : [{name: "", materialTax: totalPrice.materialTaxes}];
  const showTaxes = allTaxes.some(tax => tax.materialTax > 0);

  const roundedPrice = (price) => toFixedPrice(Math.trunc(price * 100) / 100);

  const getDiscountPercentageAmount = (discountSections, discountSection, discount, amount, forQuoteIndex) => {
    const {discountSubTotal} = getDiscountSubTotalPrice(discountSections, discountSection, discount, true, customAddedDiscounts, forQuoteIndex, hasMultipleWorkSheets);
    const priceTaken = discountSubTotal * amount / 100 // for ex. 25% from 2700 = 625
    return roundedPrice(priceTaken);
  };

  let discountsTotalAmount = 0;

  const getDiscountsTotal = (forQuoteIndex, withWizardCheck) => {
    discountSections.forEach(discountSection => {
      if (withWizardCheck && !discountSectionExistsInCurrentWizardSelections(
        discountSection,
        wizardState.allWizardsValues,
        forQuoteIndex)
      ) {
        return
      }
      const allDiscounts = sortByOrder(getDiscountsForSection(discountSection, customAddedDiscounts));
      allDiscounts.forEach(discount => {
        if (shouldCalculateDiscount(discount, forQuoteIndex, hasMultipleWorkSheets)) {
          const amount = getDiscountCurrentValue(discount, "amount", forQuoteIndex, hasMultipleWorkSheets);
          const realType = getDiscountCurrentValue(discount, "type", forQuoteIndex, hasMultipleWorkSheets);
          const discountAmount = realType === DISCOUNT_TYPES.price
            ? amount
            : getDiscountPercentageAmount(discountSections, discountSection, discount,amount, forQuoteIndex);
          discountsTotalAmount = discountsTotalAmount + (+discountAmount);
        }
      })
    })
  };

  if (discountSections.length) {
    if (hasMultipleWorkSheets) {
      quotes.forEach((quote, quoteIndex) => getDiscountsTotal(quoteIndex, true))
    } else {
      getDiscountsTotal();
    }
  }

  const differentCurrencyObjects = getDifferentCurrencyCartItems(fabricatorSettings, cartItems);
  const balanceDueObject = getSpecificDiscount(priceWorksheetState.discountSections, DISCOUNT_MAIN_TYPES.balanceDue);
  const financedAmountObject = getSpecificDiscount(priceWorksheetState.discountSections, DISCOUNT_MAIN_TYPES.financedAmount);

  return (
    <View style={styles.footer} wrap={false}>
      <View style={styles.footerLeft}>
        <PrintDocumentDisclaimer withTotal={true}
                                 showPrice={true}
                                 styles={styles}
                                 locale={locale}
                                 messages={messages}
                                 fabricatorData={fabricatorData}/>
      </View>
      <View style={styles.total}>
        {differentCurrencyObjects.length ? differentCurrencyObjects.map((currObject, currObjectIndex) => {
          let currencySubTotal = getTotalPrice(null, false, currObject.cartItems).materialPrice;
          currencySubTotal = toFixedPrice(currencySubTotal, true);
          return (
            <View style={currObjectIndex ? styles.totalSection : styles.totalSectionFirst} key={currObject.currency}>
              <Text style={styles.totalSectionText}>{messages["receipt.total.subTotal"]}:</Text>
              <Text style={styles.totalSectionAmount}>
                {getCurrencySign(currObject.currency, fabricatorSettings) + currencySubTotal}
              </Text>
            </View>
          )
        }) : null}
        <View style={differentCurrencyObjects.length ? styles.totalSection : styles.totalSectionFirst}>
          <Text style={styles.totalSectionText}>{messages["receipt.total.subTotal"]}:</Text>
          <Text style={styles.totalSectionAmount}>{getPriceWithCurrency(fabricatorSettings, subTotalPrice, null, false, true)}</Text>
        </View>
        {discountsTotalAmount ? (
          <View style={styles.totalSection}>
            <Text style={styles.totalSectionText}>{messages["receipt.total.discounts"]}:</Text>
            <Text style={styles.totalSectionAmount}>-{getPriceWithCurrency(fabricatorSettings, roundedPrice(discountsTotalAmount), null, false, true)}</Text>
          </View>
        ) : null}
        {showTaxes ? allTaxes.map((taxObject, taxObjectIndex) => {
          const taxTotal = taxObject.materialTax;
          const taxName = taxObject.nameIntl && taxObject.nameIntl[locale]
            ? taxObject.nameIntl[locale]
            : taxObject.state || messages["receipt.total.tax"]
          return (
            <View style={styles.totalSection} key={taxObjectIndex}>
              <Text style={styles.totalSectionText}>{taxName}</Text>
              <Text style={styles.totalSectionAmount}>{getPriceWithCurrency(fabricatorSettings, taxTotal, null, false, true)}</Text>
            </View>
          )
        }) : null}
        {totalPrice.installationTaxes ? (
          <View style={styles.totalSection}>
            <Text style={styles.totalSectionText}>{messages["receipt.total.installationTaxes"]}</Text>
            <Text style={styles.totalSectionAmount}>{getPriceWithCurrency(fabricatorSettings, totalPrice.installationTaxes, null, false, true)}</Text>
          </View>
        ) : null}
        {fabricatorSettings.useShipping || isCafeCountetops ? (
          <View style={styles.totalSection}>
            <Text style={styles.totalSectionText}>{messages["grohe.cart.confirm.columns.delivery"]}</Text>
            <Text style={styles.totalSectionAmount}>{getPriceWithCurrency(fabricatorSettings, totalPrice.totalDeliveryCost, null, false, true)}</Text>
          </View>
        ) : null}
        {(fabricatorSettings.deposit && fabricatorSettings.enableDeposit) || getDepositDiscount(discountSections) ? (
          <View style={styles.totalSection}>
            <Text style={styles.totalSectionText}>{messages["depositPopup.depositAmount"]}:</Text>
            <Text style={styles.totalSectionAmount}>
              {getPriceWithCurrency(fabricatorSettings,
                roundedPrice(getDepositAmount(discountSections, grandTotal, fabricatorData)),
                null, false, true)}
            </Text>
          </View>
        ) : null}
        {differentCurrencyObjects.length ? differentCurrencyObjects.map(currObject => {
          const currentTotal = getTotalPrice(null, !receiptReferral, currObject.cartItems);
          const total = toFixedPrice(getQuotePriceWithTaxes(currentTotal), true)
          return (
            <View style={styles.totalSection} key={currObject.currency}>
              <Text style={styles.totalSectionText}>{messages["receipt.total.total"]}:</Text>
              <Text style={receiptReferral ? styles.totalSectionAmount : styles.totalSectionAmountFinal}>
                {getCurrencySign(currObject.currency, fabricatorSettings) + total}
              </Text>
            </View>
          )
        }) : null}
        {priceWorksheetState.financeActiveDiscount && financedAmountObject.neededDiscount ? (
          <View style={styles.totalSection}>
            <Text style={styles.totalSectionText}>{financedAmountObject.neededDiscount.name}</Text>
            <Text style={styles.totalSectionAmount}>
              {getPriceWithCurrency(fabricatorSettings, getFinancedAmount({priceWorksheetState}, financedAmountObject.neededDiscount), null, false, true)}
            </Text>
          </View>
        ) : null}
        <View style={styles.totalSection}>
          <Text style={styles.totalSectionText}>{messages["receipt.total.total"]}:</Text>
          <Text style={receiptReferral || balanceDueObject.neededDiscount ? styles.totalSectionAmount : styles.totalSectionAmountFinal}>
            {getPriceWithCurrency(fabricatorSettings, grandTotal, null, false, true)}
          </Text>
        </View>
        {balanceDueObject.neededDiscount ? (
          <View style={styles.totalSection}>
            <Text style={styles.totalSectionText}>{balanceDueObject.neededDiscount.name}:</Text>
            <Text style={receiptReferral ? styles.totalSectionAmount : styles.totalSectionAmountFinal}>
              {getPriceWithCurrency(fabricatorSettings, getBalanceDueAmount({
                priceWorksheetState,
                quotesState: {activeQuoteIndex: 0},
                fabricatorState: fabricatorData,
              }, balanceDueObject.neededDiscountSection, balanceDueObject.neededDiscount), null, false, true)}
            </Text>
          </View>
        ) : null}
        {receiptReferral ? (
          <View style={styles.totalSection}>
            <Text style={styles.totalSectionText}>{messages["receipt.referral.amount"]} ({receiptReferral.referralPercent}%):</Text>
            <Text style={styles.totalSectionAmountFinal}>
              {/*{getPriceWithCurrency(fabricatorSettings, receiptReferral.referralAmount, null, false, true)}*/}
              {getPriceWithCurrency(fabricatorSettings, getPercentage(subTotalPrice, receiptReferral.referralPercent), null, false, true)}
            </Text>
          </View>
        ) : null}
      </View>
    </View>
  )
};

PrintDocumentTotal.propTypes = {
  priceWorksheetState: PropTypes.object.isRequired,
  fabricatorData: PropTypes.object.isRequired,
  styles: PropTypes.object.isRequired,
  cartItems: PropTypes.array.isRequired,
};

export default PrintDocumentTotal
